import React from 'react';
import { Box } from '@mui/material';
import { Speed, ToggleOn } from '@mui/icons-material';
import QuestionValueBadge from './QuestionValueBadge';

import { CampaignQuestion, QuestionType } from '../CampaignQuestionsEditor/types';

interface QuestionChipProps {
    question: CampaignQuestion;
    questionNumber: number;
    onClick: () => void;
    testMode?: boolean;
    questionInUse?: boolean;
}

/**
 * Displays a clickable question chip with colored prefix and question text.
 *
 * @param {QuestionChipProps} props
 * @returns {JSX.Element}
 */
const QuestionChip: React.FC<QuestionChipProps> = React.memo(
    ({ question, questionNumber, onClick, testMode = false, questionInUse = false }) => {
        const handleClick = () => {
            if (!testMode) {
                onClick();
            }
        };

        const questionLabel = `Q${questionNumber}`;
        const questionTitle = question.title || question.text;

        return (
            <Box
                onClick={handleClick}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'stretch',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: testMode ? 'default' : 'pointer',
                    transition: 'all 0.15s',
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                    ...(!testMode && {
                        '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover',
                        },
                    }),
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: questionInUse ? 'devblue.90' : 'gray.10',
                        color: questionInUse ? 'white.100' : 'text.primary',
                        px: 0.75,
                        minWidth: '70px',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                    }}
                >
                    {question.type === QuestionType.BOOLEAN && <ToggleOn sx={{ fontSize: '0.95rem', mr: 0.5, opacity: 0.5 }} />}
                    {question.type === QuestionType.NUMERIC && <Speed sx={{ fontSize: '0.95rem', mr: 0.5, opacity: 0.5 }} />}
                    {questionLabel}
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        py: 0.75,
                        px: 0.5,
                        pl: 0.75,
                        fontSize: '0.875rem',
                        wordBreak: 'break-word',
                        display: 'flex',
                        alignItems: 'center',
                        lineHeight: '1.25rem',
                        gap: 0.5,
                    }}
                >
                    <Box sx={{ flex: 1 }}>{questionTitle}</Box>
                    <QuestionValueBadge question={question} />
                </Box>
            </Box>
        );
    }
);

export default QuestionChip;
